import { useState, useEffect } from "react";
import { NavBar } from "@/components/NavBar";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { CreateStoreWizard } from "@/components/stores/CreateStoreWizard";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/authStore";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { CreateStoreWizardFormValues } from "@/components/stores/wizard/types";
import { supabase } from "@/integrations/supabase/client";
import { BusinessType } from "@/models/storeModel";

const RegisterPage = () => {
  const { t } = useTranslation();
  const [showStoreWizard, setShowStoreWizard] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, refreshStoreAccess } = useAuthStore();
  const { addStore } = useStoreManagementStore();
  const [isCreatingStore, setIsCreatingStore] = useState(false);

  // Effect to check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setShowStoreWizard(false);
      }
    };

    checkAuth();
  }, []);

  const handleRegistrationComplete = async () => {
    // Wait briefly for auth state to update
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Verify user is authenticated
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session) {
      setShowStoreWizard(true);
    } else {
      toast({
        title: t("error"),
        description: t("pleaseLoginFirst"),
        variant: "destructive",
      });
      navigate("/login");
    }
  };

  const handleStoreWizardComplete = async (
    formValues: CreateStoreWizardFormValues
  ) => {
    if (isCreatingStore) return;
    setIsCreatingStore(true);

    try {
      // Double check authentication
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error("No authenticated user found");
      }

      // Refresh store access before creating new store
      await refreshStoreAccess(session.user.id);

      const result = await addStore({
        name: formValues.name,
        address: `${formValues.street}, ${formValues.streetNumber}, ${
          formValues.zip
        } ${formValues.city}, ${formValues.state || "Germany"}`,
        phone: formValues.phone,
        email: formValues.email,
        is_active: true,
        isB2B: formValues.isB2B ?? true,
        legalName: formValues.legalName,
        taxId: formValues.taxId,
        businessType: formValues.businessType as BusinessType,
        registrationNumber: formValues.registrationNumber,
        industry_type: formValues.industryType,
        companyPhone: formValues.companyPhone,
      });

      if (!result) {
        throw new Error("Failed to create store");
      }

      toast({
        title: t("successfulSetup"),
        description: t("accountAndStoreCreated"),
      });

      navigate("/dashboard");
    } catch (error) {
      console.error("Error creating store:", error);
      toast({
        title: t("error"),
        description: t("failedToCreateStore"),
        variant: "destructive",
      });
    } finally {
      setIsCreatingStore(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <div className="flex-1 flex items-center justify-center p-4 bg-muted/50">
        <RegisterForm
          isStaffInvite={false}
          onAuthComplete={handleRegistrationComplete}
        />

        <CreateStoreWizard
          open={showStoreWizard}
          onOpenChange={setShowStoreWizard}
          onComplete={handleStoreWizardComplete}
        />
      </div>
    </div>
  );
};

export default RegisterPage;
